import { useState } from 'react';
import { ActionList, Frame, Navigation, Popover } from '@shopify/polaris';
import {
  HomeIcon,
  CreditCardIcon,
  ChartVerticalIcon,
  ChartLineIcon,
  SettingsIcon,
} from '@shopify/polaris-icons';
import { useLanguage } from './providers/LanguageProvider';
import { useEnsurePixel } from './hooks/useEnsurePixel';
import { useMerchantAnalytics, useTrackAdminPage } from './hooks/useMerchantAnalytics';
import type { PageName } from './types';
import DashboardPage from './pages/DashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import UsagePage from './pages/UsagePage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';

const LANGUAGES = [
  { code: 'en' as const, label: 'English', flag: '🇬🇧' },
  { code: 'it' as const, label: 'Italiano', flag: '🇮🇹' },
];

export default function App() {
  const [currentPage, setCurrentPage] = useState<PageName>('dashboard');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  // Idempotently activate the storefront Web Pixel for this shop on load.
  useEnsurePixel();

  // Product analytics for the admin itself. Same fire-and-forget posture as the
  // pixel activation above: it can fail entirely without the merchant noticing.
  useMerchantAnalytics();
  useTrackAdminPage(currentPage);

  const currentLang = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];

  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        items={[
          {
            label: t.nav.dashboard,
            icon: HomeIcon,
            onClick: () => setCurrentPage('dashboard'),
            selected: currentPage === 'dashboard',
          },
          {
            label: t.nav.subscription,
            icon: CreditCardIcon,
            onClick: () => setCurrentPage('subscription'),
            selected: currentPage === 'subscription',
          },
          {
            label: t.nav.usage,
            icon: ChartVerticalIcon,
            onClick: () => setCurrentPage('usage'),
            selected: currentPage === 'usage',
          },
          {
            label: t.nav.analytics,
            icon: ChartLineIcon,
            onClick: () => setCurrentPage('analytics'),
            selected: currentPage === 'analytics',
          },
          {
            label: t.nav.settings,
            icon: SettingsIcon,
            onClick: () => setCurrentPage('settings'),
            selected: currentPage === 'settings',
          },
        ]}
      />
    </Navigation>
  );

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={setCurrentPage} />;
      case 'subscription':
        return <SubscriptionPage onNavigate={setCurrentPage} />;
      case 'usage':
        return <UsagePage onNavigate={setCurrentPage} />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'settings':
        return <SettingsPage onNavigate={setCurrentPage} />;
      default:
        return <DashboardPage onNavigate={setCurrentPage} />;
    }
  };

  return (
    <Frame
      navigation={navigationMarkup}
      logo={{
        width: 36,
        topBarSource: '/logo.png',
        accessibilityLabel: 'Aisthetix',
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '8px 16px',
            borderBottom: '1px solid var(--p-color-border)',
            background: 'var(--p-color-bg-surface)',
          }}
        >
          <Popover
            active={langMenuOpen}
            activator={
              <button
                type="button"
                onClick={() => setLangMenuOpen((open) => !open)}
                style={{
                  background: 'none',
                  border: '1px solid var(--p-color-border)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  lineHeight: 1,
                  padding: '4px 8px',
                }}
              >
                {currentLang.flag}
              </button>
            }
            onClose={() => setLangMenuOpen(false)}
            preferredAlignment="right"
          >
            <ActionList
              items={LANGUAGES.map((lang) => ({
                content: lang.flag,
                active: language === lang.code,
                onAction: () => {
                  setLanguage(lang.code);
                  setLangMenuOpen(false);
                },
              }))}
            />
          </Popover>
        </div>
        {renderPage()}
      </div>
    </Frame>
  );
}
